import { useContext } from 'react';
import { ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { MORE_TIME_MINUTES, type MoreTimeMinutes } from '@heraion/shared';

import { Button, Sheet } from '../index';
import { ChipChoiceRow } from '../primitives/ChipChoiceRow';
import { useBreakpoint } from '../useBreakpoint';
import type { Theme } from './theme';

// #356: «More time». The athlete picks 10, 20 or 30 minutes; the list says
// what today's session takes on, one row a step, and about how long. Confirm
// writes it to today's draft only. A side panel on a wide screen, as the bell.

/** A number stays with its unit and a «3 × 8» in one piece: a line breaks between steps, never inside one. */
const keepTogether = (text: string): string => text.replace(/ ?× ?/g, '\u00A0×\u00A0').replace(/(\d) (?=\S)/g, '$1\u00A0');

export type MoreTimeRow = { key: string; name: string; detail: string };

export function MoreTimeSheet({
  visible,
  minutes,
  onMinutes,
  spent = [],
  rows,
  totalMin,
  busy,
  failed = false,
  onConfirm,
  onClose,
  t,
  theme,
}: {
  visible: boolean;
  minutes: MoreTimeMinutes;
  onMinutes: (m: MoreTimeMinutes) => void;
  /** The chips that add nothing beyond the one before: off, and the sheet says why. */
  spent?: readonly MoreTimeMinutes[];
  rows: readonly MoreTimeRow[];
  /** The rows' estimate, in whole minutes. */
  totalMin: number;
  /** The rows coming back are being built: Confirm waits. */
  busy: boolean;
  /** The last Confirm could not build the rows: the sheet stays open and says so. */
  failed?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: Theme;
}) {
  const styles = makeStyles(theme);
  const { wide } = useBreakpoint();
  const { height, fontScale } = useWindowDimensions();
  // The context, not the hook: no provider (a bare mount) means no inset.
  const insets = useContext(SafeAreaInsetsContext);
  const { sizes } = theme.typography;
  // Each off chip is the same as the live chip below it: that is its reason.
  const sameAs = (m: MoreTimeMinutes): MoreTimeMinutes | undefined =>
    [...MORE_TIME_MINUTES].reverse().find((x) => x < m && !spent.includes(x));
  const off = MORE_TIME_MINUTES.filter((m) => spent.includes(m) && sameAs(m) !== undefined);
  const first = off[0];
  const second = off[1];
  const spentLine =
    first === undefined
      ? null
      : second === undefined
        ? t('mobile.logger.moreTime.spent', { spent: first, minutes: sameAs(first), defaultValue: '+{{spent}} adds nothing over +{{minutes}} min.' })
        : t('mobile.logger.moreTime.spentBoth', {
            a: first,
            b: second,
            minutes: sameAs(first),
            defaultValue: '+{{a}} and +{{b}} add nothing over +{{minutes}} min.',
          });
  const chrome =
    (insets?.top ?? 0) +
    (insets?.bottom ?? 0) +
    theme.spacing.s4 +
    theme.spacing.s5 +
    theme.spacing.s3 * 4 +
    Math.ceil(fontScale * (sizes.lg + 4 * sizes.base) * 1.5) +
    theme.spacing.s6 +
    // The error line, two lines at most at a large font.
    (failed ? theme.spacing.s3 + Math.ceil(fontScale * sizes.sm * 1.5) * 2 : 0) +
    // The line under the chips: the two-chip form in German at 320 px and a
    // large font runs to three lines.
    (spentLine !== null ? theme.spacing.s3 + Math.ceil(fontScale * sizes.sm * 1.5) * 3 : 0);
  const bodyMax = Math.max(Math.round(height * 0.3), height - chrome);
  const cancel = t('mobile.common.cancel', { defaultValue: 'Cancel' });
  return (
    <Sheet
      theme={theme}
      visible={visible}
      onRequestClose={onClose}
      title={t('mobile.logger.moreTime.title', { defaultValue: 'More time' })}
      bottomInset={insets?.bottom ?? 0}
      edge={wide ? 'end' : 'bottom'}
    >
      {/* Title and the two buttons stay; everything between them scrolls. The
          card has no height of its own, so the body is capped by what the
          screen leaves after that chrome (it grows with the font scale). */}
      <ScrollView style={{ maxHeight: bodyMax }} contentContainerStyle={styles.body}>
        <ChipChoiceRow
          theme={theme}
          choices={MORE_TIME_MINUTES}
          selected={minutes}
          onSelect={onMinutes}
          label={(m) => `+${m}`}
          chipA11yLabel={(m) => {
            const base = t('mobile.logger.moreTime.minutesA11y', { count: m, defaultValue: '{{count}} more minutes' });
            const same = sameAs(m);
            return spent.includes(m) && same !== undefined
              ? `${base}. ${t('mobile.logger.moreTime.spentA11y', { minutes: same, defaultValue: 'Same as +{{minutes}} min, nothing more to add.' })}`
              : base;
          }}
          isDisabled={(m) => spent.includes(m)}
          groupA11yLabel={t('mobile.logger.moreTime.minutesTitle', { defaultValue: 'Extra minutes' })}
          title={t('mobile.logger.moreTime.minutesTitle', { defaultValue: 'Extra minutes' })}
        />
        {spentLine !== null ? <Text accessibilityLiveRegion="polite" style={styles.muted}>{spentLine}</Text> : null}
        {rows.length === 0 ? (
          <Text accessibilityLiveRegion="polite" style={styles.muted}>
            {t('mobile.logger.moreTime.none', { minutes, defaultValue: 'Nothing fits in {{minutes}} min.' })}
          </Text>
        ) : (
          rows.map((r) => (
            <View key={r.key} style={styles.row}>
              <Text style={styles.name}>{r.name}</Text>
              <Text style={styles.detail}>{keepTogether(r.detail)}</Text>
            </View>
          ))
        )}
        <Text style={styles.muted}>
          {t('mobile.logger.moreTime.todayOnly', {
            defaultValue: "Only today's session changes. The program stays as it is.",
          })}
        </Text>
      </ScrollView>
      {rows.length > 0 ? (
        <Text accessibilityLiveRegion="polite" style={styles.total}>
          {t('mobile.logger.moreTime.total', { minutes: totalMin, defaultValue: 'About {{minutes}} min more' })}
        </Text>
      ) : null}
      {failed ? (
        <Text accessibilityLiveRegion="polite" accessibilityRole="alert" style={styles.failed}>
          {t('mobile.logger.moreTime.failed', { defaultValue: 'Could not add it. Try again.' })}
        </Text>
      ) : null}
      <View style={styles.actions}>
        <Button
          theme={theme}
          variant="secondary"
          label={cancel}
          onPress={onClose}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 4 }}
          style={styles.action}
          numberOfLines={2}
        />
        <Button
          theme={theme}
          label={t('mobile.logger.moreTime.confirm', { defaultValue: 'Confirm' })}
          onPress={onConfirm}
          disabled={rows.length === 0}
          loading={busy}
          hitSlop={{ top: 8, bottom: 8, left: 4, right: 8 }}
          style={styles.action}
          numberOfLines={2}
        />
      </View>
    </Sheet>
  );
}

function makeStyles(theme: Theme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    body: { gap: s.s3 },
    // Name above its step: at a large font a long name never pushes the step off.
    row: {
      paddingVertical: s.s2,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
      gap: s.s1,
    },
    name: { color: p.ink, fontSize: ty.sizes.base, fontWeight: '600' },
    detail: { color: p.ink2, fontSize: ty.sizes.sm, fontFamily: ty.fonts.mono },
    total: { color: p.ink, fontSize: ty.sizes.base, fontWeight: '700', fontFamily: ty.fonts.mono },
    muted: { color: p.ink2, fontSize: ty.sizes.sm },
    failed: { color: p.danger, fontSize: ty.sizes.sm },
    actions: { flexDirection: 'row', gap: s.s3 },
    action: { flex: 1 },
  });
}
